import { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Trash2, Search, AlertTriangle, CheckCircle, XCircle, MinusCircle } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/app/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/app/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/app/components/ui/popover';
import { useGearModel } from '@/hooks/use-gear-models';
import type { Asset, Inspection, CreateInspectionPayload, FindingSeverity, InspectionResult, InspectionType, StepResult } from '@/lib/types';

const findingSchema = z.object({
  severity: z.enum(['low', 'medium', 'high']),
  description: z.string().min(1, 'Description is required'),
  location: z.string().optional(),
});

const stepResultSchema = z.object({
  step_template: z.number().nullable().optional(),
  step_number: z.number(),
  step_title: z.string(),
  is_critical: z.boolean(),
  result: z.enum(['pass', 'fail', 'na']),
  notes: z.string().optional(),
});

const inspectionFormSchema = z.object({
  asset: z.number().min(1, 'Asset is required'),
  inspection_type: z.enum(['initial', 'routine', 'post_deployment']),
  date: z.string().min(1, 'Date is required').refine((date) => {
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return selectedDate <= today;
  }, 'Date cannot be in the future'),
  result: z.enum(['pass', 'fail', 'conditional']),
  notes: z.string().optional(),
  findings: z.array(findingSchema),
  step_results: z.array(stepResultSchema),
  signer_name: z.string().optional(),
  pin: z.string().optional(),
});

type InspectionFormValues = z.infer<typeof inspectionFormSchema>;

interface InspectionFormProps {
  inspection?: Inspection;
  assets: Asset[];
  assetsLoading?: boolean;
  preselectedAssetId?: number;
  onSearchAssets?: (query: string) => void;
  onSubmit: (data: CreateInspectionPayload) => void;
  isSubmitting?: boolean;
  onCancel?: () => void;
}

export function InspectionForm({
  inspection,
  assets,
  assetsLoading = false,
  preselectedAssetId,
  onSearchAssets,
  onSubmit,
  isSubmitting = false,
  onCancel,
}: InspectionFormProps) {
  const [assetSearchOpen, setAssetSearchOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [assetSearchValue, setAssetSearchValue] = useState('');
  const [stepsPopulated, setStepsPopulated] = useState(!!inspection);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<InspectionFormValues>({
    resolver: zodResolver(inspectionFormSchema),
    defaultValues: {
      asset: inspection?.asset || 0,
      inspection_type: inspection?.inspection_type || 'routine',
      date: inspection?.date || new Date().toISOString().split('T')[0],
      result: inspection?.result || 'pass',
      notes: inspection?.notes || '',
      findings: inspection?.findings?.map((f) => ({
        severity: f.severity,
        description: f.description,
        location: f.location || '',
      })) || [],
      step_results: inspection?.step_results?.map((s) => ({
        step_template: s.step_template,
        step_number: s.step_number,
        step_title: s.step_title,
        is_critical: s.is_critical,
        result: s.result,
        notes: s.notes || '',
      })) || [],
      signer_name: '',
      pin: '',
    },
  });

  const { fields: findingFields, append: appendFinding, remove: removeFinding } = useFieldArray({
    control,
    name: 'findings',
  });

  const { fields: stepFields, replace: replaceSteps, update: updateStep } = useFieldArray({
    control,
    name: 'step_results',
  });

  const gearModelId = selectedAsset?.gear_model ?? 0;
  const { data: gearModel } = useGearModel(gearModelId);

  const watchedResult = watch('result');
  const watchedStepResults = watch('step_results');

  useEffect(() => {
    if (inspection) {
      reset({
        asset: inspection.asset,
        inspection_type: inspection.inspection_type || 'routine',
        date: inspection.date,
        result: inspection.result || 'pass',
        notes: inspection.notes || '',
        findings: inspection.findings?.map((f) => ({
          severity: f.severity,
          description: f.description,
          location: f.location || '',
        })) || [],
        step_results: inspection.step_results?.map((s) => ({
          step_template: s.step_template,
          step_number: s.step_number,
          step_title: s.step_title,
          is_critical: s.is_critical,
          result: s.result,
          notes: s.notes || '',
        })) || [],
      });
      const asset = assets.find((a) => a.id === inspection.asset);
      if (asset) {
        setSelectedAsset(asset);
      }
    }
  }, [inspection, assets, reset]);

  useEffect(() => {
    if (preselectedAssetId && !selectedAsset && !inspection) {
      const asset = assets.find((a) => a.id === preselectedAssetId);
      if (asset) {
        setSelectedAsset(asset);
        setValue('asset', asset.id);
      }
    }
  }, [preselectedAssetId, assets, selectedAsset, inspection, setValue]);

  // Populate step_results from gear model templates when asset is selected (create mode only)
  useEffect(() => {
    if (stepsPopulated || !selectedAsset) return;
    // If the asset has no gear model, clear any leftover steps immediately
    if (!selectedAsset.gear_model) {
      replaceSteps([]);
      setStepsPopulated(true);
      return;
    }
    if (!gearModel) return; // still loading
    if (gearModel.inspection_steps && gearModel.inspection_steps.length > 0) {
      const steps = gearModel.inspection_steps.map((t) => ({
        step_template: t.id,
        step_number: t.step_number,
        step_title: t.title,
        is_critical: t.is_critical,
        result: 'pass' as const,
        notes: '',
      }));
      replaceSteps(steps);
    } else {
      replaceSteps([]);
    }
    setStepsPopulated(true);
  }, [gearModel, selectedAsset, stepsPopulated, replaceSteps]);

  useEffect(() => {
    if (watchedStepResults) {
      const hasCriticalFailure = watchedStepResults.some(
        (step) => step.is_critical && step.result === 'fail'
      );
      if (hasCriticalFailure && watchedResult !== 'fail') {
        setValue('result', 'fail');
      }
    }
  }, [watchedStepResults, watchedResult, setValue]);

  const handleAssetSelect = (asset: Asset) => {
    setSelectedAsset(asset);
    setValue('asset', asset.id);
    setAssetSearchOpen(false);
    // Reset step population so templates load for the new asset
    if (!inspection) {
      setStepsPopulated(false);
    }
  };

  const handleFormSubmit = (values: InspectionFormValues) => {
    const payload: CreateInspectionPayload = {
      asset: values.asset,
      inspection_type: values.inspection_type as InspectionType,
      date: values.date,
      result: values.result as InspectionResult,
      notes: values.notes || '',
      findings: values.findings.map((f) => ({
        severity: f.severity as FindingSeverity,
        description: f.description,
        location: f.location || '',
      })),
      step_results: values.step_results.map((s) => ({
        step_template: s.step_template,
        step_number: s.step_number,
        step_title: s.step_title,
        is_critical: s.is_critical,
        result: s.result as StepResult,
        notes: s.notes || '',
      })),
      signer_name: values.signer_name || undefined,
      pin: values.pin || undefined,
    };

    onSubmit(payload);
  };

  const addFinding = () => {
    appendFinding({ severity: 'low', description: '', location: '' });
  };

  const handleStepResultChange = (index: number, result: StepResult) => {
    const step = stepFields[index];
    updateStep(index, { ...step, result });
  };

  const handleStepNotesChange = (index: number, notes: string) => {
    const step = stepFields[index];
    updateStep(index, { ...step, notes });
  };

  const getTodayString = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Inspection Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Asset *</Label>
            <Popover open={assetSearchOpen} onOpenChange={setAssetSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={assetSearchOpen}
                  className="w-full justify-between"
                  disabled={isSubmitting || !!inspection}
                >
                  {selectedAsset ? (
                    <span>
                      {selectedAsset.display_name} ({selectedAsset.serial_number})
                    </span>
                  ) : (
                    <span className="text-gray-500">Select an asset...</span>
                  )}
                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Search by serial number..."
                    value={assetSearchValue}
                    onValueChange={(value) => {
                      setAssetSearchValue(value);
                      onSearchAssets?.(value);
                    }}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {assetsLoading ? 'Loading...' : 'No assets found.'}
                    </CommandEmpty>
                    <CommandGroup>
                      {assets.map((asset) => (
                        <CommandItem
                          key={asset.id}
                          value={`${asset.display_name} ${asset.serial_number}`}
                          onSelect={() => handleAssetSelect(asset)}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{asset.display_name}</span>
                            <span className="text-sm text-gray-500">
                              {asset.serial_number}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {errors.asset && (
              <p className="text-sm text-red-500">{errors.asset.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Inspection Type *</Label>
            <Select
              value={watch('inspection_type')}
              onValueChange={(value) => setValue('inspection_type', value as 'initial' | 'routine' | 'post_deployment')}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select inspection type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="initial">
                  <span className="inline-flex items-center">
                    <span className="w-2 h-2 rounded-full bg-blue-500 mr-2" />
                    Initial - New equipment verification
                  </span>
                </SelectItem>
                <SelectItem value="routine">
                  <span className="inline-flex items-center">
                    <span className="w-2 h-2 rounded-full bg-green-500 mr-2" />
                    Routine - Regular scheduled inspection
                  </span>
                </SelectItem>
                <SelectItem value="post_deployment">
                  <span className="inline-flex items-center">
                    <span className="w-2 h-2 rounded-full bg-orange-500 mr-2" />
                    Post-Deployment - After use inspection
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            {errors.inspection_type && (
              <p className="text-sm text-red-500">{errors.inspection_type.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Inspection Date *</Label>
              <Input
                id="date"
                type="date"
                max={getTodayString()}
                {...register('date')}
                disabled={isSubmitting}
              />
              {errors.date && (
                <p className="text-sm text-red-500">{errors.date.message}</p>
              )}
              <p className="text-sm text-gray-500">Cannot be a future date</p>
            </div>

            <div className="space-y-2">
              <Label>Result *</Label>
              <RadioGroup
                value={watchedResult}
                onValueChange={(value) => setValue('result', value as 'pass' | 'fail' | 'conditional')}
                className="flex gap-4"
                disabled={isSubmitting}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pass" id="pass" />
                  <Label htmlFor="pass" className="font-normal cursor-pointer">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Pass
                    </span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="fail" id="fail" />
                  <Label htmlFor="fail" className="font-normal cursor-pointer">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Fail
                    </span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="conditional" id="conditional" />
                  <Label htmlFor="conditional" className="font-normal cursor-pointer">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Conditional
                    </span>
                  </Label>
                </div>
              </RadioGroup>
              {errors.result && (
                <p className="text-sm text-red-500">{errors.result.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Enter any additional notes about this inspection..."
              rows={4}
              {...register('notes')}
              disabled={isSubmitting}
            />
            {errors.notes && (
              <p className="text-sm text-red-500">{errors.notes.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {stepFields.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Inspection Steps</CardTitle>
            <CardDescription>
              Complete each inspection step. Critical steps (marked with warning icon) will auto-fail the inspection if they fail.
            </CardDescription>
            {watchedStepResults?.some((s) => s.is_critical && s.result === 'fail') && (
              <div className="mt-2 flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  <strong>Advisory:</strong> A critical step has failed — the overall inspection result will be recorded as <strong>Fail</strong>.
                  The inspector may still review and sign off.
                </span>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {stepFields.map((step, index) => (
              <div
                key={step.id}
                className={`p-4 rounded-lg border ${
                  step.is_critical ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-500">
                        Step {step.step_number}
                      </span>
                      {step.is_critical && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          <AlertTriangle className="h-3 w-3" />
                          Critical
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium text-gray-900">{step.step_title}</h4>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant={watchedStepResults?.[index]?.result === 'pass' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleStepResultChange(index, 'pass')}
                      disabled={isSubmitting}
                      className={
                        watchedStepResults?.[index]?.result === 'pass'
                          ? 'bg-green-600 hover:bg-green-700'
                          : ''
                      }
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Pass
                    </Button>
                    <Button
                      type="button"
                      variant={watchedStepResults?.[index]?.result === 'fail' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleStepResultChange(index, 'fail')}
                      disabled={isSubmitting}
                      className={
                        watchedStepResults?.[index]?.result === 'fail'
                          ? 'bg-red-600 hover:bg-red-700'
                          : ''
                      }
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Fail
                    </Button>
                    <Button
                      type="button"
                      variant={watchedStepResults?.[index]?.result === 'na' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleStepResultChange(index, 'na')}
                      disabled={isSubmitting}
                      className={
                        watchedStepResults?.[index]?.result === 'na'
                          ? 'bg-gray-600 hover:bg-gray-700'
                          : ''
                      }
                    >
                      <MinusCircle className="h-4 w-4 mr-1" />
                      N/A
                    </Button>
                  </div>
                </div>
                {watchedStepResults?.[index]?.result === 'fail' && (
                  <div className="mt-3">
                    <Input
                      placeholder="Add notes about this failure..."
                      value={watchedStepResults?.[index]?.notes || ''}
                      onChange={(e) => handleStepNotesChange(index, e.target.value)}
                      disabled={isSubmitting}
                      className="text-sm"
                    />
                  </div>
                )}
                {step.is_critical && watchedStepResults?.[index]?.result === 'fail' && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
                    <AlertTriangle className="h-4 w-4" />
                    Critical step failure - inspection will be marked as failed
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Findings</CardTitle>
          <Button type="button" variant="outline" size="sm" onClick={addFinding}>
            <Plus className="h-4 w-4 mr-2" />
            Add Finding
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {findingFields.length === 0 ? (
            <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
              <p>No findings added yet.</p>
              <p className="text-sm mt-1">Click "Add Finding" to record any issues found during inspection.</p>
            </div>
          ) : (
            findingFields.map((field, index) => (
              <Card key={field.id} className="border-l-4 border-l-gray-300">
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start mb-4">
                    <span className="text-sm font-medium text-gray-500">Finding #{index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFinding(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Severity *</Label>
                        <Select
                          value={watch(`findings.${index}.severity`)}
                          onValueChange={(value) =>
                            setValue(`findings.${index}.severity`, value as 'low' | 'medium' | 'high')
                          }
                          disabled={isSubmitting}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select severity" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">
                              <span className="inline-flex items-center">
                                <span className="w-2 h-2 rounded-full bg-yellow-500 mr-2" />
                                Low
                              </span>
                            </SelectItem>
                            <SelectItem value="medium">
                              <span className="inline-flex items-center">
                                <span className="w-2 h-2 rounded-full bg-orange-500 mr-2" />
                                Medium
                              </span>
                            </SelectItem>
                            <SelectItem value="high">
                              <span className="inline-flex items-center">
                                <span className="w-2 h-2 rounded-full bg-red-500 mr-2" />
                                High
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {errors.findings?.[index]?.severity && (
                          <p className="text-sm text-red-500">
                            {errors.findings[index]?.severity?.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Location on Equipment</Label>
                        <Input
                          placeholder="e.g., 3m from end, near splice"
                          {...register(`findings.${index}.location`)}
                          disabled={isSubmitting}
                        />
                        {errors.findings?.[index]?.location && (
                          <p className="text-sm text-red-500">
                            {errors.findings[index]?.location?.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Description *</Label>
                      <Textarea
                        placeholder="Describe the finding in detail..."
                        rows={2}
                        {...register(`findings.${index}.description`)}
                        disabled={isSubmitting}
                      />
                      {errors.findings?.[index]?.description && (
                        <p className="text-sm text-red-500">
                          {errors.findings[index]?.description?.message}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {/* Signature / Sign-Off Section — only on create, not edit */}
      {!inspection && <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Sign-Off (Optional)
          </CardTitle>
          <CardDescription>
            Provide your name to sign off and lock the inspection immediately.
            Leave blank to save as draft.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="signer_name">Typed Name Affirmation</Label>
            <Input
              id="signer_name"
              placeholder="Type your full name to affirm this inspection"
              {...register('signer_name')}
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pin">Signing PIN (if configured)</Label>
            <Input
              id="pin"
              type="password"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit signing PIN"
              {...register('pin')}
              disabled={isSubmitting}
            />
          </div>
        </CardContent>
      </Card>}

      <div className="flex justify-end gap-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : inspection ? 'Update Inspection' : 'Create Inspection'}
        </Button>
      </div>
    </form>
  );
}
